import React, { useState, useCallback } from "react";
import * as Sentry from "@sentry/react";

import AvatarIcon from "../Icons/AvatarIcon";
import { buildCdnUrl } from "../../../app/common/constants";
import MentorBanner from "../Icons/MentorBanner";
import NbyulaAvatarIcon from "../Icons/NbyulaAvatarIcon";
import UniversityOfficialBanner from "../Icons/UniversityOfficialBanner";
import InitialsFallback from "./initialsFallback";

const AvatarComponent = React.forwardRef(
  (
    {
      url = "",
      dimensions = "w-12 h-12",
      className = "",
      imgLoaded,
      setImgLoaded,
      imgLoadError = false,
      setImgLoadError = () => {},
      showMentorBanner = false,
      showUniversityOfficialBanner = false,
      showNbyulaFallBackAvatar = false,
      fallbackName,
      ...props
    },
    ref
  ) => {
    const isS3Thumbnail = Boolean(url && url.split("/")[0] === "public");
    const [measuredSize, setMeasuredSize] = useState(null);

    /**
     * Measure the actual rendered size of the container element.
     * Uses a callback ref merged with the forwarded ref so we read
     * the real pixel dimensions regardless of how they were set
     * (Tailwind classes, inline styles, parent constraints, etc.).
     */
    const measureRef = useCallback(
      (node) => {
        // Forward the ref to the parent
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
        if (node && isS3Thumbnail) {
          const { offsetWidth, offsetHeight } = node;
          const size = Math.max(offsetWidth, offsetHeight);
          if (size > 0) {
            setMeasuredSize(size);
          }
        }
      },
      [ref, isS3Thumbnail]
    );

    const showBanner = (showMentorBanner, showUniversityOfficialBanner) => {
      if (showUniversityOfficialBanner) {
        return (
          <div className="absolute">
            <UniversityOfficialBanner className={dimensions} />
          </div>
        );
      } else if (showMentorBanner) {
        return (
          <div className="absolute">
            <MentorBanner className={dimensions} />
          </div>
        );
      }
    };

    if (!url || imgLoadError) {
      // When we know the entity name, prefer the deterministic initials
      // swatch over the generic Nbyula / person icon — a coloured "S" reads
      // as "this is Shouvik's Space" far faster than an anonymous logo.
      const useInitials = Boolean(fallbackName && fallbackName.trim());
      return (
        <div
          ref={ref}
          className={`relative flex-shrink-0 rounded-full bg-gray-50 bg-cover bg-center ${dimensions}`}
        >
          {showMentorBanner || showUniversityOfficialBanner
            ? showBanner(showMentorBanner, showUniversityOfficialBanner)
            : null}
          <div
            className={`flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-cover bg-center object-cover ${className}`}
          >
            {useInitials ? (
              <InitialsFallback
                name={fallbackName}
                dimensions={dimensions}
              />
            ) : showNbyulaFallBackAvatar ? (
              <NbyulaAvatarIcon className={dimensions} />
            ) : (
              <AvatarIcon dimensions={dimensions} />
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={measureRef}
        className={`relative flex-shrink-0 rounded-full bg-gray-50 bg-cover bg-center ${dimensions}`}
      >
        <div
          className={`h-full w-full overflow-hidden rounded-full bg-cover bg-center object-cover ${className}`}
        >
          {!imgLoaded && (
            <div
              className={`shimmer ${dimensions} absolute top-0 left-0 rounded-full`}
            />
          )}
          {showMentorBanner || showUniversityOfficialBanner
            ? showBanner(showMentorBanner, showUniversityOfficialBanner)
            : null}
          <img
            className={
              "h-full w-full rounded-full object-cover" +
              (imgLoaded ? "" : " invisible")
            }
            src={buildCdnUrl(url, measuredSize)}
            alt=""
            onLoad={() => setImgLoaded(true)}
            onError={(err) => {
              Sentry.captureException(new Error(err));
              setImgLoaded(false);
              setImgLoadError(true);
            }}
            {...props}
            loading="lazy"
          />
        </div>
      </div>
    );
  }
);

AvatarComponent.displayName = "AvatarComponent";

export default AvatarComponent;
